<template>
    <ul
        v-bind:id="listId"
        ref="filteredList"
        aria-expanded="true"
        role="listbox"
        data-test-role="filteredSelectItemList"
        style="list-style-type: none; padding: 0"
        class="position-relative mt-2">
        <li
            v-for="(wrapper, index) in itemWrappers"
            v-bind:id="getItemId(index)"
            v-bind:key="wrapper.item[valueProp]"
            ref="listItem"
            v-bind:aria-selected="index === focusIndex"
            role="option"
            v-bind:class="getItemClassObj(wrapper.item, index)"
            data-test-role="filteredSelectItem"
            v-on:mouseover="mouseOverFocusIndex(index)"
            v-on:click="onOptionSelected(wrapper.item, index)">
            <div class="d-flex justify-content-between">
                <div
                    v-bind:title="wrapper.item[props.textProp]"
                    v-bind:aria-label="wrapper.item[props.textProp]">
                    <slot
                        v-bind="wrapper"
                        name="optionDisplay" />
                </div>
                <div class="d-flex align-items-center">
                    <div
                        v-if="selectedModelValue == wrapper.item[props.valueProp]"
                        class="badge clear-selection"
                        v-bind:aria-label="clearSelectionAriaLabel"
                        tabindex="-1">
                        <pc-bs-icon name="x-circle-fill" />
                    </div>
                </div>
            </div>
        </li>
        <li
            v-if="itemWrappers.length <= 0"
            v-bind:class="getItemClassObj()">
            No items found
        </li>
    </ul>
</template>

<script setup>
    import { computed, ref, watch, useTemplateRef, onMounted, nextTick } from "vue";
    import { globalGenerator } from "Util/uniqueIdGenerator";
    import { useResource } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./filteredSelectOptions.i18n";
    import { escapeRegex } from "Util/util";

    const props = defineProps({
        selectedModelValue: { type: [String, Number], required: false, default: null },
        filteredItems: { type: Array, required: true },
        searchTerm: { type: String, required: true },
        valueProp: { type: String, required: false, default: "value" },
        textProp: { type: String, required: false, default: "text" },
        listId: { type: String, required: true }
    });

    const emit = defineEmits(["onSelect", "setActiveDescendantId"]);

    const focusIndex = ref(0);
    const filteredList = useTemplateRef("filteredList");

    // i18n
    const { clearSelectionAriaLabel } = useResource(KEYS, NAMESPACE);

    // unique prefix from globalIdGenerator
    // => once we have unique prefix, use to create any number of unique ids (ex. using index of option)
    const prefix = globalGenerator.getNextId("filtered-select-options-prefix");

    const itemWrappers = computed(() => {
        const itemWrappersList = [];
        for (let i = 0; i < props.filteredItems.length; i++) {
            itemWrappersList.push({
                item: props.filteredItems[i],
                highlighted: createHighlightedText(props.filteredItems[i])
            });
        }
        return itemWrappersList;
    });

    onMounted(() => {
        // on open, scroll to selected item if it exists
        // use == instead of === to account for modelValues that are not strings
        const selectedIndex = props.filteredItems.findIndex(
            (item) => item[props.valueProp] == props.selectedModelValue
        );
        if (selectedIndex !== -1) {
            focusIndex.value = selectedIndex;
            emit("setActiveDescendantId", getItemId(focusIndex.value));
            scrollToActiveItem();
        }
    });

    function getItemId(index) {
        return prefix + index;
    }

    function createHighlightedText(item) {
        // split on empty string splits every character, needs special handling to prevent this
        if (!props.searchTerm.length == 0) {
            // create regex object from props.searchTerm to handle split, case insensitive
            const splitOnRegex = new RegExp(`(${escapeRegex(props.searchTerm)})`, `i`);
            const splitItemText = item[props.textProp].split(splitOnRegex);

            const highlightedSubstringOptionList = [];
            const lowerCaseSearchTerm = props.searchTerm.toLowerCase();
            for (let i = 0; i < splitItemText.length; i++) {
                if (splitItemText[i].toLowerCase() == lowerCaseSearchTerm) {
                    highlightedSubstringOptionList.push({
                        DisplayText: splitItemText[i],
                        ShowHighlight: true,
                        SubIndexOrder: i
                    });
                } else {
                    highlightedSubstringOptionList.push({
                        DisplayText: splitItemText[i],
                        ShowHighlight: false,
                        SubIndexOrder: i
                    });
                }
            }
            return highlightedSubstringOptionList;
        }
        return [{ DisplayText: item[props.textProp], ShowHighlight: false, SubIndexOrder: 0 }];
    }

    function moveFocusUp() {
        focusIndex.value = Math.max(0, focusIndex.value - 1);
        // emit new event to be caught by filteredSelect
        // focus has changed => new activeDescendantId is stored
        emit("setActiveDescendantId", getItemId(focusIndex.value));
        scrollToActiveItem();
    }

    function moveFocusDown() {
        focusIndex.value = Math.min(props.filteredItems.length - 1, focusIndex.value + 1);
        // emit new event to be caught by filteredSelect
        // focus has changed => new activeDescendantId is stored
        emit("setActiveDescendantId", getItemId(focusIndex.value));
        scrollToActiveItem();
    }

    function mouseOverFocusIndex(index) {
        focusIndex.value = index;
        // emit new event to be caught by filteredSelect
        // focus has changed => new activeDescendantId is stored
        emit("setActiveDescendantId", getItemId(focusIndex.value));
    }

    const scrollToActiveItem = () => {
        const selectedOption = document.getElementById(getItemId(focusIndex.value));
        if (selectedOption) {
            selectedOption.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
    };

    watch(itemWrappers, () => {
        // if focusIndex is out of bounds, reset it
        // set focusIndex to 0 if filteredItems is empty, otherwise set to last item in list
        if (props.filteredItems.length <= focusIndex.value) {
            mouseOverFocusIndex(props.filteredItems.length === 0 ? 0 : props.filteredItems.length - 1);
        }
        if (focusIndex.value > 0) {
            // Wait for DOM to update before scrolling to prevent potentially scrolling to an unrendered item
            nextTick(() => {
                scrollToActiveItem();
            });
        }
    });

    function onOptionSelected(item, index) {
        // nothing to select if no items are available
        if (props.filteredItems.length > 0) {
            // clear selection if option is reselected
            // use == instead of === to account for modelValues that are not strings
            if (props.selectedModelValue == props.filteredItems[focusIndex.value][props.valueProp]) {
                emit("onSelect", null);
            }
            // index will be null if selected via enter key
            // in order to utilize focusIndex
            else if (index) {
                focusIndex.value = index;
                emit("onSelect", item);
            } else {
                emit("onSelect", props.filteredItems[focusIndex.value]);
            }
        }
    }

    // two kinds of highlighting needed => currently selected vs selecting an option
    function getItemClassObj(item, index) {
        // use == instead of === to account for modelValues that are not strings
        const isSelected = item?.[props.valueProp] == props.selectedModelValue;
        const isFocused = index === focusIndex.value;
        return {
            "my-1": true,
            "me-1": true,
            "p-1": true,
            "filter-item-key-highlight": item && isFocused,
            "filter-item-selected": item && isSelected,
            "filter-no-item": !item
        };
    }

    // methods accessible to Pc-Filtered-Select component
    defineExpose({ moveFocusUp, moveFocusDown, onOptionSelected });
</script>

<style lang="scss" scoped>
    .filter-no-item {
        border-color: transparent;
        font-style: italic;
    }

    .filter-item-key-highlight {
        background-color: var(--bs-primary-100);
        border-color: var(--bs-primary);
    }

    .filter-item-selected {
        background-color: var(--bs-primary-200);
        border-color: var(--bs-primary);
    }

    .filter-item-key-highlight.filter-item-selected {
        .clear-selection {
            color: var(--bs-primary-500);
        }
    }

    .options-wrapper {
        border: 1px solid var(--pc-input-border-color);
        border-top: none;
        border-bottom-right-radius: var(--pc-input-border-radius);
        border-bottom-left-radius: var(--pc-input-border-radius);
    }
    .clear-selection {
        background-color: transparent;
        color: var(--bs-gray-100);
        margin: 0 0.25rem;
        border: 0;
        cursor: default;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        height: 1.75rem;
        width: 1.75rem;
        position: relative; /* Make position relative for absolute positioning of the icon */
    }

    .clear-selection svg {
        /* Ensure the icon is centered and fully visible */
        position: absolute;
        width: 1.4rem;
        height: 1.4rem;
    }
</style>
